import { EnvironmentConfiguration, LogService } from '@absg/angular-common';
import { AuthenticationService, IdleHandlerService, ILogoutHandler } from '@absg/angular-platform';
import { EnvironmentService as SharedEnvironmentService, NimAlertService } from '@absg/nim-client-shared';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ResponseTypes } from 'core.enums/response-types.enum';
import { MiniResponse } from 'core.models/mini-response';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, take, tap } from 'rxjs/operators';
import { CleanupEvents } from 'shared.root/constants/clen-up-events';
import { ElectronApplicationService } from 'shared.services/electron-application.service';

import { AccessPointService } from './access-point/access-point.service';
import { StationInfoService } from './station-info.service';

@Injectable({ providedIn: 'root' })
export class StationAuthenticationService implements ILogoutHandler {
  private ipcRenderer: Electron.IpcRenderer;
  private cancelEvent = new BehaviorSubject<boolean>(false);
  cancelEvent$ = this.cancelEvent.asObservable();
  isStartingLogout$ = new Subject<void>();
  private isSignOutComplete: BehaviorSubject<boolean>;
  private settings: EnvironmentConfiguration;
  private invalidPracticeUser = new BehaviorSubject<boolean>(false);
  private jwtState: string;
  response = new Subject<MiniResponse>();
  parentWindow: Electron.BrowserWindow;
  authWindow: Electron.BrowserWindow;
  private authTimeout: NodeJS.Timeout;
  private loginTimeout: NodeJS.Timeout;
  logoutWindow: Electron.BrowserWindow;
  dismissModalSubject = new Subject<void>();
  private hasShownLogin = false;
  private loginScreenNumber = 0;
  private logoutClosed$ = new BehaviorSubject<boolean>(false);
  // create new electron window that opens to a route that displays a component that immediately logs in
  // once we log in close this new window and route the main window to dispense (or where ever)

  // can we check router events and prevent redirects? either from angular or electron We need to make sure that if the interceptor tries to log you back in we send you to logout
  // or find a way to tell the interceptor where to redirect

  constructor(
    private zone: NgZone,
    private router: Router,
    private electronApplicationService: ElectronApplicationService,
    private environment: EnvironmentConfiguration,
    private accessPointService: AccessPointService,
    private loggingService: LogService,
    private authService: AuthenticationService,
    private idleHandler: IdleHandlerService,
    private stationInfoService: StationInfoService,
    private currentEnvironment: SharedEnvironmentService,
    private nimAlertService: NimAlertService,
  ) {
    this.parentWindow = this.electronApplicationService.getCurrentWindow();

    this.isSignOutComplete = new BehaviorSubject<boolean>(true);

    this.response.subscribe((response) => {
      this.handleResponse(response);
    });

    this.response.next(new MiniResponse(ResponseTypes.Init));

    // don't listen for events if not running inside electron
    if (this.electronApplicationService.isRunningElectron()) {
      // NOTE: These events are defined in electron-cleanup.js
      this.ipcRenderer = this.electronApplicationService.getIpcRenderer();
      this.listen();
    }

    authService.setLogoutHandler(this);

    authService.isLoggedIn$.subscribe(x => {
      if (x) {
        this.isSignOutComplete.next(false); // reset the value that we've signed out each time we log in
        this.ipcRenderer?.send(CleanupEvents.resetSignout);
      }
    });
  }

  get userStoreKey(): string {
    if (this.settings && this.settings.authority && this.settings.client_id) {
      return `user:${this.settings.authority}:${this.settings.client_id}`;
    } else {
      return null;
    }
  }

  redirectToLoginPage(): Observable<void> {
    this.loginScreenNumber++;
    console.log('Starting redirectToLoginPage ', this.loginScreenNumber);

    if (!this.electronApplicationService.isRunningElectron()) {
      return this.authService.redirectToLoginPage();
    }

    if (this.authWindow) {
      console.error('login page already exists', this.loginScreenNumber);
      this.loggingService.logTrace(
        `Station Auth Service - signinRedirect - attempt to create another auth window - title: ${this.authWindow.getTitle()}`);
    }

    this.authWindow = !this.authWindow ? this.getBrowserWindow() : this.authWindow;
    this.authWindow.webContents.on('console-message', (_event, level, message, _line, _sourceId) => {
      console.log(`From Auth: L${level} - ${message}`);
    });

    this.addCrashProtection(this.authWindow);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.authWindow.webContents.on('did-navigate', (_event: any, newUrl: string) => {
      console.log('redirectToLoginPage did-nav ', this.loginScreenNumber, newUrl);
      const isB2c = newUrl.indexOf('b2clogin.com') > -1;
      const isResetPassword = newUrl.indexOf('passwordreset') > -1;
      if (isB2c || isResetPassword) {
        if (!this.hasShownLogin) {
          clearTimeout(this.authTimeout);
          this.hasShownLogin = true;
          console.log('showing auth window');
          this.authWindow.show();
        }
      } else { // we don't want to show the login-start or jwt-return pages to the user
        this.authWindow.hide();
      }
      if (this.jwtState != null && (newUrl.indexOf('login-start.html') > -1 || newUrl.indexOf('final-landing.html') > -1) && this.authWindow && !this.authWindow.isDestroyed()) {
        // navigated back to login-start, so now we expect to have completed the process
        // telling angular to steal the state
        console.log('redirectToLoginPage did-nav ', this.loginScreenNumber);
        this.zone.run(() => {
          this.router.navigateByUrl(`/authstart?#state=${this.jwtState}`);
        });
        this.destroyAuthWindow();
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.authWindow.webContents.on('will-redirect', (_event: any, newUrl: string) => {
      console.log('redirectToLoginPage will-redirect ', this.loginScreenNumber);
      const stateIndex = newUrl.indexOf('#state');
      if (stateIndex > -1) {
        // save off state?
        this.jwtState = newUrl.substring(stateIndex + 7);
        const fullState = newUrl.substring(stateIndex);
        if (this.stationInfoService.getIsPackaged()) {
          // when in packaged mode take the redirect and send to local file
          const directory = this.stationInfoService.getPackageDirectory();
          const jwtUrl = `file://${directory}/auth-redirect/jwt-return.html${fullState}`;
          _event.preventDefault(); // keep this here? // cancel the existing redirect?
          this.authWindow.loadURL(jwtUrl);
        }
      } else {
        this.destroyAuthWindow();
      }

      this.loggingService.logTrace('Station Auth Service - signinRedirect - event: will-redirect');
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.authWindow.webContents.on('did-start-navigation', (_event: any, newUrl: string) => {
      // will-redirect does not catch internal same root navigation. So when reset hits it redirects to same base URL. Which does not trigger any other hooks.
      // Adding this to close the auth window if it routes home, since it should only host B2C and Reset Portal Page.
      console.log('did start nav -', newUrl);
      const isRedirectingToHome = newUrl.indexOf('/home') > -1;
      if (isRedirectingToHome) {
        this.authWindow.destroy();
      }
    });
    this.authWindow.on('closed', () => {
      console.log('redirectToLoginPage auth closed ', this.loginScreenNumber);

      this.loggingService.logTrace('Station Auth Service - signinRedirect - event: closed');
      this.zone.run(() => {
        this.cancelEvent.next(true);
      });

      this.hasShownLogin = true; // in the case that you are already logged in and it never shows the auth window
      this.authWindow = null;
    });
    this.authWindow.on('show', () => {
      console.log('auth window shown');
    });

    this.loggingService.logTrace('Station Auth Service - signinRedirect - loadURL - auth-start');

    let url = 'http://localhost:44328/auth-redirect/login-start.html';
    if (this.stationInfoService.getIsPackaged()) {
      const directory = this.stationInfoService.getPackageDirectory();
      url = `file://${directory}/auth-redirect/login-start.html`;
    }
    url = this.addUrlEnv(url);
    console.log('redirectToLoginPage loadUrl ', this.loginScreenNumber);
    const loadAndShow = this.authWindow.loadURL(url, { extraHeaders: 'pragma: no-cache' });
    // we show this window after it navigates or after 5 seconds
    this.hasShownLogin = false;

    this.authTimeout = setTimeout(() => {
      console.log(`looking at timeout to show auth window - hasShown = ${this.hasShownLogin}`);
      // sometimes the login window doesn't seem to show, so show it after a bit
      if (!this.hasShownLogin) {
        this.hasShownLogin = true;
        this.authWindow.show();
      }
    }, 15000);

    this.loginTimeout = setTimeout(() => {
      // if login takes longer than 2 minutes close the window, somehow
      if (!this.authWindow) {
        return;
      }

      this.destroyAuthWindow();
    }, 2 * 60 * 1000);

    return from(loadAndShow);
  }

  private destroyAuthWindow(): void {
    console.log('destroying auth window');
    clearTimeout(this.loginTimeout);
    this.jwtState = null;
    this.authWindow.close();
    this.authWindow.destroy();
  }

  redirectToLogoutPage(fromLogoutButton = false): Observable<unknown> {
    if (this.logoutWindow) {
      this.logoutWindow.destroy(); // should not ever fire, but just in case
      this.logoutWindow = null;
    }
    console.log('logging out ', this.loginScreenNumber);
    this.isStartingLogout$.next();
    this.resetLogoutWatchers();

    this.accessPointService.executeShutDownDoorHandling();

    // if cancel is clicked in legal disclaimer
    // then do not call prepare from logout as we haven't logged in fully yet
    const logoutHandler$ = fromLogoutButton ? this.idleHandler.prepareForLogout() : of(void 0);

    return logoutHandler$.pipe(
      take(1),
      tap(() => console.log('after logouthandler')),
      concatMap(() => {
        if (this.electronApplicationService.isRunningElectron()) {
          this.configureLogoutWindow();
          return of(null).pipe(
            concatMap(() => this.logoutClosed$.pipe(filter(x => x), take(1), map(() => { }))));
        } else {
          this.sendMiniResponse();
          return this.authService.logoutPopup();
        }
      }),
      // concatMap(() => this.logoutClosed$.pipe(filter(x => x), take(1), map(() => { }))),
      tap(() => {
        console.log('logout handler tap');
        this.authService.prepareForLogout();
        this.authService.logoutStation();
        this.sendSignoutCompletedMessage();
      }),
      concatMap(() => this.authService.clearMsalCache$()),
      tap(() => {
        console.log('logout handler cancelEvent next');
        this.cancelEvent.next(true);
      }),
    );
  }

  private configureLogoutWindow(): void {
    this.logoutWindow = this.getBrowserWindow();
    let logoutRedirectCount = 0;

    /*
      Using will navigate so that we can cancel the navigation.
      What we really care about is that we've come back from logging out.
      If we do not cancel nav it will attempt to load localhost on the logout popup.
    */
    this.logoutWindow.webContents.on('will-navigate', (event: Electron.Event, newUrl: string) => {
      this.loggingService.logTrace(`Station Auth Service - signoutRedirect - event: did-navigate url: ${newUrl}`);
      console.log(`logging out did-nav to ${newUrl}`, this.loginScreenNumber);
      if (newUrl.indexOf('logout-start.html') > -1) {
        logoutRedirectCount++;
      }

      if (logoutRedirectCount === 2 && this.logoutWindow && !this.logoutWindow.isDestroyed()) {
        // need to check the path we're on for logout
        // maybe even add a redirect for after logout?
        console.log('logging out did-nav closing ', this.loginScreenNumber);
        event.preventDefault(); // cancel the navigation

        this.logoutWindow.destroy();
      }
    });

    this.addCrashProtection(this.logoutWindow);

    // reset the logoutWindow on close
    this.logoutWindow.on('closed', () => {
      console.log('logging out did-nav closed ', this.loginScreenNumber);
      this.loggingService.logTrace('Station Auth Service - signoutRedirect - event: closed');
      this.logoutWindow = null;

      this.zone.run(() => {
        this.sendMiniResponse();
        console.log('next logoutClosed$');
        this.logoutClosed$.next(true);
      });
    });

    this.loggingService.logTrace('Station Auth Service - signoutRedirect - loadURL');
    let url = 'http://localhost:44328/auth-redirect/logout-start.html';
    if (this.stationInfoService.getIsPackaged()) {
      const directory = this.stationInfoService.getPackageDirectory();
      url = `file://${directory}/auth-redirect/logout-start.html`;
    }
    url = this.addUrlEnv(url);

    this.logoutWindow.loadURL(url, { extraHeaders: 'pragma: no-cache' }).then(() => {
      this.zone.run(() => {
        console.log('... logout url loaded');
      });

      // so what if we don't show and just let the redirect happen?
      // huh - so not showing as it'll still run the js and redirect and work as we need it without showing the window
      // this.logoutWindow.show(); // keeping in case of future debugging
    });
  }

  private sendMiniResponse(): void {
    const miniResponse = new MiniResponse(ResponseTypes.LogOut);
    miniResponse.success = true;
    this.response.next(miniResponse);
    // added to close the Close door prompt on auto logout
    this.dismissModalSubject.next();
    this.sendSignoutCompletedMessage();
  }

  private addCrashProtection(authWindow: Electron.BrowserWindow): void {
    authWindow.webContents.on('certificate-error', (
      // event: Electron.Event, curl: string, error: string, certificate: Electron.Certificate, callback: (isTrusted: boolean) => void, isMainFrame: boolean,
    ) => {
      console.error('AuthWindow: certificate error, reloading...');
      this.reloadLogoutWindow(authWindow);
    });

    // this is what 'crashed' says we should use
    authWindow.webContents.on('render-process-gone', (
      //event: Electron.Event, details: Electron.RenderProcessGoneDetails,
    ) => {
      console.error('AuthWindow: render-process-gone');
      this.handleElectronWindowCrash(authWindow);
    });

    authWindow.webContents.on('destroyed', () => {
      console.log('AuthWindow: destroyed'); // so we destroy in normal events
    });

    authWindow.webContents.on('did-fail-load', (
      _event: Electron.Event, _errorCode: number, _errorDescription: string, _validatedURL: string, _isMainFrame: boolean, _frameProcessId: number, _frameRoutingId: number,
    ) => {
      console.error('AuthWindow: did-fail-load');
      this.reloadLogoutWindow(authWindow);
    });

    authWindow.webContents.on('did-fail-provisional-load', (
      _event: Electron.Event, errorCode: number, errorDescription: string, _validatedURL: string, _isMainFrame: boolean, _frameProcessId: number, _frameRoutingId: number,
    ) => {
      console.error(`AuthWindow: did-fail-provisional-load - code: ${errorCode}, desc: ${errorDescription}`);
      this.reloadLogoutWindow(authWindow);
    });

    authWindow.webContents.on('plugin-crashed', () => {
      console.log('AuthWindow: plugin-crashed');
      this.reloadLogoutWindow(authWindow);
    });

    authWindow.webContents.on('preload-error', () => {
      console.log('AuthWindow: preload-error');
      this.reloadLogoutWindow(authWindow);
    });

    authWindow.webContents.on('unresponsive', () => {
      console.log('AuthWindow: unresponsive');
      this.handleElectronWindowCrash(authWindow);
    });
  }

  reloadLogoutWindow(authWindow: Electron.BrowserWindow): void {
    console.log('reloading logout window');
    authWindow.webContents.reload();
  }

  handleElectronWindowCrash(authWindow: Electron.BrowserWindow) {
    console.log('handling auth window crash state');
    this.sendSignoutCompletedMessage();
    this.cancelEvent.next(true);
    authWindow.destroy();
    this.logoutWindow = null;
    this.authWindow = null;
    this.jwtState = null;
    this.nimAlertService.showErrorAlert('Login window error: please try again');
  }

  private addUrlEnv(url: string) {
    return `${url}?env=${this.currentEnvironment.getEnvironmentName()}`;
  }

  // this is called when you alt+F4 or close the window
  private cleanupRequest(): void {
    this.accessPointService.executeShutDownDoorHandling();

    this.authService.isLoggedIn$.pipe(
      take(1),
      concatMap(isLoggedIn => {
        if (!isLoggedIn) {
          return of(void 0);
        }
        // if we're logged in, make sure we close PO
        return this.redirectToLogoutPage(true).pipe(
          catchError(e => {
            console.error(e);
            this.isSignOutComplete.next(true);
            return of(void 0);
          }),
          concatMap(() => this.isSignOutComplete.pipe(filter(x => x))));
      }),
      tap(() => {
        this.ipcRenderer?.send(CleanupEvents.mainModuleDestroyedNotification);
      }),
    ).subscribe();
  }

  private getBrowserWindow(): Electron.BrowserWindow {
    const parentWidth = this.parentWindow.getSize()[0];
    const parentHeight = this.parentWindow.getSize()[1];

    const browserWindowOptions = {
      width: parentWidth,
      height: parentHeight,
      maxWidth: parentWidth,
      maxHeight: parentHeight,
      show: false,
      frame: false,
      fullscreen: this.parentWindow.isFullScreen(),
      parent: this.parentWindow,
      modal: true,
      webPreferences: {
        enableRemoteModule: true,
        contextIsolation: false,
        spellcheck: false,
        nodeIntegration: false, // Don't turn the nodeIntegration to true, or else the electron window
        // which is generated for the B2c login page will throw error for Jquery not defined
        // session: this.parentWindow.webContents.session,
      },
    };

    const browser = this.electronApplicationService.createBrowserWindow(browserWindowOptions);
    browser.on('unresponsive', () => {
      this.handleElectronWindowCrash(browser);
    });

    // browser.webContents.openDevTools(); // for debugging
    return browser;
  }

  private handleResponse(response: MiniResponse): void {
    // don't attempt to process init messages
    if (response.responseType === ResponseTypes.Init) {
      return;
    }

    if (!response.success) {
      this.loggingService.logTrace(`Station Auth Service - handleResponse - ${response.message}`);
      return;
    }

    this.zone.run(() => {
      if (response.responseType === ResponseTypes.LogIn) {
        this.router.navigate(['/auth'], { fragment: 'no_fragment' });
      } else if (response.responseType === ResponseTypes.LogOut) {
        this.router.navigateByUrl('login');
      }
    });
  }

  private listen(): void {
    this.ipcRenderer?.on(CleanupEvents.cleanupRequest, () => this.cleanupRequest());
  }

  private sendSignoutCompletedMessage(): void {
    this.ipcRenderer?.send(CleanupEvents.signoutCompleted);
    this.isSignOutComplete.next(true);
  }

  loadNewEnvironment(): void {
    if (!this.environmentSettingsAreValid(this.environment)) {
      throw new RangeError('environment.openIdSettings must be populated');
    }
  }

  private environmentSettingsAreValid(environment: EnvironmentConfiguration): boolean {
    const authority = environment.openIdSettings.authority;
    return !!authority && authority !== '';
  }

  setInvalidPracticeUser(value: boolean): void {
    this.invalidPracticeUser.next(value);
  }

  getInvalidPracticeUser(): Observable<boolean> {
    return this.invalidPracticeUser.asObservable();
  }

  private resetLogoutWatchers(): void {
    this.logoutClosed$.next(false);
  }
}
